import { useCallback, useReducer } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useFirestore } from '../../utils/firebase';
import { useAuth } from '../../auth';
import { apiClient } from '../../api-client';
import { COLLECTIONS } from '../../constants/firestoreCollections';

const GENERIC_ERROR = 'Error loading document. Please try again.';

const STATUS = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

const INITIAL = {
  open: false,
  requestId: null,
  storagePaths: [],
  status: STATUS.LOADING,
  errorMessage: '',
  retryable: false,
};

const viewerReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN':
      return {
        open: true,
        requestId: action.requestId,
        storagePaths: action.storagePaths ?? [],
        status: STATUS.LOADING,
        errorMessage: '',
        retryable: false,
      };
    case 'READY':
      return { ...state, status: STATUS.READY };
    case 'ERROR':
      return {
        ...state,
        status: STATUS.ERROR,
        errorMessage: action.message,
        retryable: action.retryable ?? false,
      };
    case 'CLOSE':
      return { ...state, open: false };
    default:
      return state;
  }
};

const useResolutionViewer = ({ onAcknowledged } = {}) => {
  const { getToken } = useAuth();
  const database = useFirestore();
  const [viewer, dispatch] = useReducer(viewerReducer, INITIAL);

  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);

  const acknowledgeAndReveal = useCallback(
    (requestId, storagePaths) => {
      dispatch({ type: 'OPEN', requestId, storagePaths });
      apiClient('POST', '/vacation/resolution/acknowledge', getToken, { requestId })
        .then(() => {
          onAcknowledged?.(requestId);
          dispatch({ type: 'READY' });
        })
        .catch(() => dispatch({ type: 'ERROR', message: GENERIC_ERROR, retryable: true }));
    },
    [getToken, onAcknowledged],
  );

  const openById = useCallback(
    requestId => {
      dispatch({ type: 'OPEN', requestId });
      getDoc(doc(database, COLLECTIONS.VACATION_REQUESTS, requestId))
        .then(snapshot => {
          const data = snapshot.exists() ? snapshot.data() : null;
          if (!data || data.resolution?.status !== 'sent') {
            dispatch({
              type: 'ERROR',
              message: 'This resolution is no longer available.',
              retryable: false,
            });
            return;
          }
          acknowledgeAndReveal(
            requestId,
            data.resolution.pdfs.map(pdf => pdf.storagePath),
          );
        })
        .catch(() => dispatch({ type: 'ERROR', message: GENERIC_ERROR, retryable: true }));
    },
    [database, acknowledgeAndReveal],
  );

  const openWithPaths = useCallback(
    (requestId, storagePaths) => acknowledgeAndReveal(requestId, storagePaths),
    [acknowledgeAndReveal],
  );

  const retry = () => {
    if (!viewer.requestId) return;
    if (viewer.storagePaths.length) {
      acknowledgeAndReveal(viewer.requestId, viewer.storagePaths);
    } else {
      openById(viewer.requestId);
    }
  };

  const viewerProps = {
    open: viewer.open,
    onClose: close,
    storagePaths: viewer.storagePaths,
    ready: viewer.status === STATUS.READY,
    error: viewer.status === STATUS.ERROR,
    errorMessage: viewer.errorMessage,
    retryable: viewer.retryable,
    onRetry: retry,
  };

  return { viewerProps, openById, openWithPaths };
};

export { useResolutionViewer };
